import classNames from 'classnames';
import React, { FC, forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import defaultImage from 'src/assets/images/default-icon.png';
import { DEPTH, FavoriteCoinType } from 'src/common/constants';
import {
    changeScrollBehavior,
    currencyFormatter,
    currencyValue,
    digits,
    isEmptyObj,
    stringReform,
} from 'src/common/helpers';
import {
    ClickAwayListener,
    ModalContent,
    Tooltip,
    SimplifiedOrProTradeSwitcher,
} from 'src/components/core';
import {
    ArrowDown,
    Favorites,
    Day,
    More,
    Night,
    MoreDots,
    Favorite,
    Info,
} from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { useAuth } from 'src/context/AuthContext';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { APP_URLS } from 'src/setup/client/app-urls';
import { updateThemeData } from 'src/setup/redux/slices/globalSlice';
import { fetchTokenInfo } from 'src/setup/redux/slices/spotSlice';
import { createFavorite, cancelFavorite } from 'src/setup/redux/slices/userSlice';
import { CurrencyValue, DepthType, TradeSocketResponseData } from 'src/types';
import { Token } from 'src/types/configV2';

import {
    PairInfoName,
    PairInfoOperationIcon,
    PairListIndex,
    PairListIInfo,
    CoinInfoModal,
} from './mobilePairInfo';
import {
    PairInfoContainer,
    PairName,
    Bar,
    PairInfoModuleWrapper,
    PairInfoModule,
    ThemeSwitchWrapper,
    CoinInfoTooltip,
} from './pairInfo';
import { CoinDrawer } from '../coin-drawer';
import { PreferencesDrawer } from '../preferences-drawer';

const PairInfo: FC = () => {
    const { t } = useTranslation();
    const { isMobile, colors, isDark } = useTheme();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isDrawerPreferencesOpen, setIsPreferencesDrawerOpen] = useState(false);
    const [isCoinInfoModalOpen, setIsCoinInfoModalOpen] = useState(false);
    const [brokerToken, setBrokerToken] = useState({} as TradeSocketResponseData);
    const tokenInfoData = useAppSelector((state) => state.socket.slowBroker);
    const { currentSymbol, tokenInfo } = useAppSelector((state) => state.spot);
    const { supportLanguages, token } = useAppSelector((state) => state.global.configV2);
    const { tokenRates } = useAppSelector((state) => state.global);
    const { favorites } = useAppSelector((state) => state.user);
    const { isAuthenticated } = useAuth();

    const minPricePrecision: keyof DepthType = currentSymbol?.minPricePrecision || '0';
    const coinInfo = token ? token[currentSymbol.baseTokenId] : ({} as Token);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (isDrawerOpen) {
            changeScrollBehavior(isDrawerOpen);
        }
    }, [isMobile]);

    useEffect(() => {
        if (!isEmptyObj(currentSymbol)) {
            dispatch(fetchTokenInfo({ token_id: currentSymbol.baseTokenId }));
        }
    }, [currentSymbol, dispatch]);

    useEffect(() => {
        let infoObj = tokenInfoData[currentSymbol.symbolId];
        if (infoObj) {
            setBrokerToken(infoObj);
        } else {
            setBrokerToken({} as TradeSocketResponseData);
        }
    }, [tokenInfoData, currentSymbol]);

    const changeThemeMode = () => {
        dispatch(updateThemeData({ isDark: !isDark }));
    };

    const drawerToggler = () => {
        changeScrollBehavior(!isDrawerOpen);
        setIsDrawerOpen(!isDrawerOpen);
    };

    const currencyValueCal = () => {
        if (!isEmptyObj(tokenRates) && supportLanguages && !isEmptyObj(brokerToken)) {
            let valueInCurrency: CurrencyValue = currencyValue(
                tokenRates[currentSymbol.quoteTokenId],
                Number(brokerToken.c),
                supportLanguages,
                true,
            );
            return `≈${valueInCurrency.value} ${valueInCurrency.endName}`;
        }
        return '--';
    };

    const favoriteClicked = (symbolId: string) => {
        const body = {
            symbol_ids: symbolId,
            coin_type: FavoriteCoinType.SPOT,
            exchange_id: currentSymbol.exchangeId,
        };
        if (!favorites.includes(symbolId)) {
            dispatch(createFavorite({ body, isLogin: isAuthenticated }));
        } else {
            dispatch(cancelFavorite({ body, isLogin: isAuthenticated }));
        }
    };

    const togglePreferencesDrawer = () => {
        setIsPreferencesDrawerOpen(!isDrawerPreferencesOpen);
        changeScrollBehavior(!isDrawerPreferencesOpen);
    };

    const coinInfoPanel = () => {
        return (
            <CoinInfoTooltip>
                <span>{t('currency_information')}</span>
                <FlexContainer>
                    <FlexContainer>
                        <img
                            src={coinInfo?.iconUrl || defaultImage}
                            alt={coinInfo?.tokenFullName}
                        />
                        {coinInfo?.tokenName}&nbsp;
                        <span>({coinInfo?.tokenFullName})</span>
                    </FlexContainer>
                    <FlexContainer>
                        <FlexContainer>
                            <span>{t('max_supply')}</span>
                            <span>{t('release_date')}</span>
                            <span>{t('website')}</span>
                        </FlexContainer>
                        <FlexContainer>
                            <span>{tokenInfo.maxQuantitySupplied || '--'}</span>
                            <span>{tokenInfo.publishTime || '--'}</span>
                            <span>
                                {tokenInfo.officialWebsiteUrl ? (
                                    <a
                                        href={tokenInfo.officialWebsiteUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {tokenInfo.officialWebsiteUrl}
                                    </a>
                                ) : (
                                    '--'
                                )}
                            </span>
                        </FlexContainer>
                    </FlexContainer>
                    <FlexContainer>
                        <span>{t('introduction')}</span>

                        {tokenInfo.description ? (
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: stringReform(tokenInfo.description),
                                }}
                            />
                        ) : (
                            ''
                        )}
                    </FlexContainer>
                    <FlexContainer>
                        <a
                            href={`${APP_URLS.TOKEN_INFO}${tokenInfo.tokenId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {t('learn_more_about', { token: tokenInfo.tokenName })}
                        </a>
                    </FlexContainer>
                </FlexContainer>
            </CoinInfoTooltip>
        );
    };

    // eslint-disable-next-line react/display-name
    const ModalContentWithRef = forwardRef(() => <ModalContent>{coinInfoPanel()}</ModalContent>);
    return (
        <>
            {isDrawerOpen && (
                <CoinDrawer drawerToggler={drawerToggler} isDrawerOpen={isDrawerOpen} />
            )}
            {isMobile ? (
                <FlexContainer
                    styles={{
                        flexDirection: 'column',
                        padding: '24.5px 16px 16px',
                    }}
                >
                    <FlexContainer styles={{ justifyContent: 'flex-end', width: '100%' }}>
                        <SimplifiedOrProTradeSwitcher isDerivative={false} />
                    </FlexContainer>
                    <FlexContainer
                        styles={{
                            width: '100%',
                            justifyContent: 'space-between',
                            marginBottom: '10px',
                            padding: '8.5px',
                        }}
                    >
                        <PairInfoName>
                            <FlexContainer onClick={drawerToggler}>
                                <More
                                    fill={colors.text.primary}
                                    stroke={colors.text.primary}
                                    className={'clickable'}
                                />
                                <span>
                                    <span>{currentSymbol.baseTokenName}</span>/
                                    {currentSymbol.quoteTokenName}
                                </span>
                            </FlexContainer>
                            <Info
                                fill={colors.text.tertiary}
                                className={'clickable'}
                                onClick={() => setIsCoinInfoModalOpen(true)}
                            />
                        </PairInfoName>
                        <PairInfoOperationIcon>
                            {favorites.includes(currentSymbol.symbolId) ? (
                                <Favorites
                                    stroke={colors.primary}
                                    fill={colors.primary}
                                    onClick={() => favoriteClicked(currentSymbol.symbolId)}
                                    className={'clickable'}
                                />
                            ) : (
                                <Favorite
                                    stroke={colors.text.primary}
                                    onClick={() => favoriteClicked(currentSymbol.symbolId)}
                                    className={'clickable'}
                                />
                            )}
                            {isDark ? (
                                <Day
                                    stroke={colors.text.primary}
                                    onClick={changeThemeMode}
                                    className={'clickable'}
                                />
                            ) : (
                                <Night
                                    stroke={colors.text.primary}
                                    onClick={changeThemeMode}
                                    className={'clickable'}
                                />
                            )}
                            {isAuthenticated && (
                                <MoreDots
                                    stroke={colors.text.primary}
                                    className={'clickable'}
                                    onClick={togglePreferencesDrawer}
                                />
                            )}
                            <PreferencesDrawer
                                isOpen={isDrawerPreferencesOpen}
                                toggleDrawer={togglePreferencesDrawer}
                            />
                        </PairInfoOperationIcon>
                    </FlexContainer>
                    <FlexContainer
                        styles={{
                            width: '100%',
                            justifyContent: 'space-between',
                            marginBottom: '10px',
                            padding: '8.5px',
                        }}
                    >
                        <PairListIndex>
                            <li className={Number(brokerToken?.m) >= 0 ? 'up' : 'down'}>
                                <div>
                                    {brokerToken.c
                                        ? currencyFormatter(
                                              digits(
                                                  Number(brokerToken.c),
                                                  DEPTH[minPricePrecision],
                                              ),
                                              DEPTH[minPricePrecision],
                                          )
                                        : '0.00'}
                                </div>
                            </li>
                            <li>
                                <>
                                    {currencyValueCal()}
                                    <span className={Number(brokerToken.m) >= 0 ? 'up' : 'down'}>
                                        {Number(brokerToken.m) > 0 ? '+' : ''}
                                        {!isNaN(Number(brokerToken?.m))
                                            ? `${currencyFormatter(
                                                  Math.floor(Number(brokerToken.m) * 10000) / 100,
                                                  2,
                                              )}%`
                                            : '--'}
                                    </span>
                                </>
                            </li>
                        </PairListIndex>
                        <FlexContainer>
                            <PairListIInfo>
                                <li>{t('24_high')}</li>
                                <li>{t('24_low')}</li>
                                <li>{t('24_turn_over')}</li>
                            </PairListIInfo>
                            <PairListIInfo>
                                <li>
                                    {Number(brokerToken?.h) >= 0
                                        ? currencyFormatter(
                                              digits(
                                                  Number(brokerToken.h),
                                                  DEPTH[minPricePrecision],
                                              ),
                                              DEPTH[minPricePrecision],
                                          )
                                        : '--'}
                                </li>
                                <li>
                                    {Number(brokerToken?.l) >= 0
                                        ? currencyFormatter(
                                              digits(
                                                  Number(brokerToken.l),
                                                  DEPTH[minPricePrecision],
                                              ),
                                              DEPTH[minPricePrecision],
                                          )
                                        : '--'}
                                </li>
                                <li>
                                    {Number(brokerToken?.qv) >= 0
                                        ? `$${currencyFormatter(
                                              Number(brokerToken.qv),
                                              DEPTH[minPricePrecision],
                                          )}`
                                        : '--'}
                                </li>
                            </PairListIInfo>
                        </FlexContainer>
                    </FlexContainer>
                </FlexContainer>
            ) : (
                <FlexContainer
                    styles={{
                        justifyContent: 'space-between',
                        padding: '8px 32px 8px 24px',
                        height: '51px',
                        borderBottom: `1px solid ${colors.border.light}`,
                        borderTop: `1px solid ${colors.border.light}`,
                    }}
                >
                    <PairInfoContainer>
                        <FlexContainer className="PairInfo" styles={{ cursor: 'pointer' }}>
                            <PairName>
                                <FlexContainer onClick={drawerToggler}>
                                    <div>
                                        <span>
                                            <span>{currentSymbol.baseTokenName}</span>/
                                            {currentSymbol.quoteTokenName}
                                        </span>
                                    </div>
                                    {currentSymbol.label && currentSymbol.label.labelValue ? (
                                        <div
                                            style={
                                                currentSymbol.label.colorCode
                                                    ? {
                                                          color: currentSymbol.label.colorCode,
                                                          borderColor:
                                                              currentSymbol.label.colorCode,
                                                      }
                                                    : {}
                                            }
                                            className={'label'}
                                        >
                                            {currentSymbol.label.labelValue}
                                        </div>
                                    ) : (
                                        ''
                                    )}
                                    <ArrowDown
                                        stroke={colors.text.primary}
                                        className={'clickable'}
                                    />
                                </FlexContainer>
                                <Tooltip
                                    overlay={coinInfoPanel()}
                                    placement="bottom-start"
                                    offset={
                                        currentSymbol.label && currentSymbol.label.labelValue
                                            ? [-146, 19]
                                            : [-116, 19]
                                    }
                                    interactive={true}
                                    arrow={false}
                                    className={'coin-info-tooltip'}
                                >
                                    <Info fill={colors.text.tertiary} className={'clickable'} />
                                </Tooltip>
                            </PairName>
                        </FlexContainer>
                        <Bar height={'36px'} margin={'0 16px'} />
                        <PairInfoModule
                            className={classNames(
                                Number(brokerToken?.m) >= 0 ? 'up' : 'down',
                                'heading',
                            )}
                        >
                            {brokerToken.c
                                ? currencyFormatter(
                                      digits(Number(brokerToken.c), DEPTH[minPricePrecision]),
                                      DEPTH[minPricePrecision],
                                  )
                                : '0.00'}
                        </PairInfoModule>
                        <PairInfoModuleWrapper>
                            <PairInfoModule>
                                <span>{`${t('24h_change')} %`}</span>
                                <span
                                    className={
                                        Number(brokerToken.m) >= 0
                                            ? 'up'
                                            : Number(brokerToken.m) < 0
                                            ? 'down'
                                            : ''
                                    }
                                >
                                    {Number(brokerToken.m) > 0 ? '+' : ''}
                                    {!isNaN(Number(brokerToken.m))
                                        ? `${currencyFormatter(
                                              Math.floor(Number(brokerToken.m) * 10000) / 100,
                                              2,
                                          )}%`
                                        : '--'}
                                </span>
                            </PairInfoModule>
                            <PairInfoModule>
                                <span>{t('24_high')}</span>
                                <span>
                                    {Number(brokerToken?.h)
                                        ? currencyFormatter(
                                              digits(
                                                  Number(brokerToken.h),
                                                  DEPTH[minPricePrecision],
                                              ),
                                              DEPTH[minPricePrecision],
                                          )
                                        : '--'}
                                </span>
                            </PairInfoModule>
                            <PairInfoModule>
                                <span>{t('24_low')}</span>
                                <span>
                                    {Number(brokerToken?.l)
                                        ? currencyFormatter(
                                              digits(
                                                  Number(brokerToken.l),
                                                  DEPTH[minPricePrecision],
                                              ),
                                              DEPTH[minPricePrecision],
                                          )
                                        : '--'}
                                </span>
                            </PairInfoModule>
                            <PairInfoModule>
                                <span>{t('24_volume')}</span>
                                <span>
                                    {Number(brokerToken?.v) >= 0
                                        ? currencyFormatter(
                                              digits(
                                                  Number(brokerToken.v),
                                                  DEPTH[currentSymbol?.basePrecision],
                                              ),
                                              DEPTH[currentSymbol?.basePrecision],
                                          )
                                        : '--'}
                                </span>
                            </PairInfoModule>
                            <PairInfoModule>
                                <span>
                                    {t('24_turnover', { token: currentSymbol.quoteTokenName })}
                                </span>
                                <span>
                                    {Number(brokerToken?.qv) >= 0
                                        ? currencyFormatter(
                                              Number(brokerToken.qv),
                                              DEPTH[minPricePrecision],
                                          )
                                        : '--'}
                                </span>
                            </PairInfoModule>
                        </PairInfoModuleWrapper>
                    </PairInfoContainer>
                    <PairInfoContainer>
                        <ThemeSwitchWrapper>
                            <SimplifiedOrProTradeSwitcher isDerivative={false} />

                            <Bar height={'20px'} margin={'0 12px'} />
                            {favorites.includes(currentSymbol.symbolId) ? (
                                <Favorites
                                    stroke={colors.primary}
                                    fill={colors.primary}
                                    onClick={() => favoriteClicked(currentSymbol.symbolId)}
                                    className={'clickable'}
                                />
                            ) : (
                                <Favorite
                                    stroke={colors.text.primary}
                                    onClick={() => favoriteClicked(currentSymbol.symbolId)}
                                    className={'clickable'}
                                />
                            )}
                            <Bar height={'20px'} margin={'0 12px'} />
                            {isDark ? (
                                <Day
                                    stroke={colors.text.primary}
                                    onClick={changeThemeMode}
                                    className={'clickable'}
                                />
                            ) : (
                                <Night
                                    stroke={colors.text.primary}
                                    onClick={changeThemeMode}
                                    className={'clickable'}
                                />
                            )}
                        </ThemeSwitchWrapper>
                    </PairInfoContainer>
                </FlexContainer>
            )}
            {isMobile && (
                <CoinInfoModal size={'sm'} open={isCoinInfoModalOpen}>
                    <ClickAwayListener onClickAway={() => setIsCoinInfoModalOpen(false)}>
                        <ModalContentWithRef />
                    </ClickAwayListener>
                </CoinInfoModal>
            )}
        </>
    );
};

export { PairInfo };
